/**
 * Utility functions for calculating time period dates.
 * All named period functions accept an optional availabilityCutoff date.
 * When provided, periods are resolved based on the dataset's availability
 * boundary rather than the current calendar date.
 */

export interface TimePeriodDates {
  startDate: string;
  endDate: string;
}

/**
 * Get the latest complete month dates.
 * Returns the last month whose end date falls on or before the cutoff, so
 * "latest" means the most recent month with a full data set available.
 * e.g. cutoff 2026-04-15 → March 2026 (ended 2026-03-31), not April 2026 (still in progress).
 */
export function getLatestMonthDates(cutoff: Date = new Date()): TimePeriodDates {
  const cutoffTime = cutoff.getTime();
  let year = cutoff.getFullYear();
  let month = cutoff.getMonth(); // 0-11, current month
  // Walk backwards until we find a month whose end date is on or before the cutoff
  for (let i = 0; i < 24; i++) {
    const mEnd = new Date(year, month + 1, 0);
    if (mEnd.getTime() <= cutoffTime) {
      return {
        startDate: formatDate(new Date(year, month, 1)),
        endDate: formatDate(mEnd),
      };
    }
    month -= 1;
    if (month < 0) {
      month = 11;
      year -= 1;
    }
  }
  // Fallback (should never happen in practice)
  return {
    startDate: formatDate(new Date(year, month, 1)),
    endDate: formatDate(new Date(year, month + 1, 0)),
  };
}

/**
 * Get the previous complete month dates (one month before the latest complete month).
 */
export function getPreviousMonthDates(cutoff: Date = new Date()): TimePeriodDates {
  const latest = getLatestMonthDates(cutoff);
  // Parse as local time to avoid UTC offset shifting the date into the wrong month
  const latestStart = new Date(latest.startDate + "T00:00:00");
  let prevYear = latestStart.getFullYear();
  let prevMonth = latestStart.getMonth() - 1;
  if (prevMonth < 0) {
    prevMonth = 11;
    prevYear -= 1;
  }

  return {
    startDate: formatDate(new Date(prevYear, prevMonth, 1)),
    endDate: formatDate(new Date(prevYear, prevMonth + 1, 0)),
  };
}

/**
 * Get the latest complete quarter dates.
 * Returns the last quarter whose end date falls on or before the cutoff, so
 * "latest" means the most recent quarter with a full data set available.
 * e.g. cutoff 2026-04-15 → Q1 2026 (ended 2026-03-31), not Q2 2026 (still in progress).
 */
export function getLatestQuarterDates(
  cutoff: Date = new Date(),
): TimePeriodDates {
  const year = cutoff.getFullYear();
  // Quarter end dates in ascending order for the current and prior year
  const candidates = [
    { startMonth: 9, endMonth: 11, year: year - 1 }, // Q4 prev year
    { startMonth: 0, endMonth: 2, year },             // Q1
    { startMonth: 3, endMonth: 5, year },             // Q2
    { startMonth: 6, endMonth: 8, year },             // Q3
    { startMonth: 9, endMonth: 11, year },            // Q4
  ];
  // Find the last candidate whose quarter end date is on or before the cutoff
  const cutoffTime = cutoff.getTime();
  for (let i = candidates.length - 1; i >= 0; i--) {
    const { startMonth, endMonth, year: qYear } = candidates[i];
    const qEnd = new Date(qYear, endMonth + 1, 0); // last day of endMonth
    if (qEnd.getTime() <= cutoffTime) {
      return {
        startDate: formatDate(new Date(qYear, startMonth, 1)),
        endDate: formatDate(qEnd),
      };
    }
  }
  // Fallback: return Q4 of the year before previous (should never happen in practice)
  return {
    startDate: formatDate(new Date(year - 2, 9, 1)),
    endDate: formatDate(new Date(year - 2, 12, 0)),
  };
}

/**
 * Get the previous complete quarter dates (one quarter before the latest complete quarter).
 */
export function getPreviousQuarterDates(
  cutoff: Date = new Date(),
): TimePeriodDates {
  const latest = getLatestQuarterDates(cutoff);
  // Parse as local time to avoid UTC offset shifting the date into the wrong quarter
  const latestStart = new Date(latest.startDate + "T00:00:00");
  let prevYear = latestStart.getFullYear();
  let prevIndex = Math.floor(latestStart.getMonth() / 3) - 1;
  if (prevIndex < 0) {
    prevIndex = 3;
    prevYear -= 1;
  }
  const prevStartMonth = prevIndex * 3;
  const prevEndMonth = (prevIndex + 1) * 3 - 1;

  return {
    startDate: formatDate(new Date(prevYear, prevStartMonth, 1)),
    endDate: formatDate(new Date(prevYear, prevEndMonth + 1, 0)),
  };
}

/**
 * Get the latest complete semester dates. S1: Jan–Jun, S2: Jul–Dec.
 * Returns the last semester whose end date falls on or before the cutoff, so
 * "latest" means the most recent semester with a full data set available.
 * e.g. cutoff 2026-09-15 → S1 2026 (ended 2026-06-30), not S2 2026 (still in progress).
 */
export function getLatestSemesterDates(
  cutoff: Date = new Date(),
): TimePeriodDates {
  const year = cutoff.getFullYear();
  const candidates = [
    { startMonth: 6, endMonth: 11, year: year - 1 }, // S2 prev year
    { startMonth: 0, endMonth: 5, year },             // S1
    { startMonth: 6, endMonth: 11, year },            // S2
  ];
  const cutoffTime = cutoff.getTime();
  for (let i = candidates.length - 1; i >= 0; i--) {
    const { startMonth, endMonth, year: sYear } = candidates[i];
    const sEnd = new Date(sYear, endMonth + 1, 0);
    if (sEnd.getTime() <= cutoffTime) {
      return {
        startDate: formatDate(new Date(sYear, startMonth, 1)),
        endDate: formatDate(sEnd),
      };
    }
  }
  // Fallback: S2 of two years ago
  return {
    startDate: formatDate(new Date(year - 2, 6, 1)),
    endDate: formatDate(new Date(year - 2, 12, 0)),
  };
}

/**
 * Get the previous complete semester dates (one semester before the latest complete semester).
 */
export function getPreviousSemesterDates(
  cutoff: Date = new Date(),
): TimePeriodDates {
  const latest = getLatestSemesterDates(cutoff);
  // Parse as local time to avoid UTC offset shifting the date into the wrong semester
  const latestStart = new Date(latest.startDate + "T00:00:00");
  const isS2 = latestStart.getMonth() === 6;
  const prevYear = isS2
    ? latestStart.getFullYear()
    : latestStart.getFullYear() - 1;
  const prevStartMonth = isS2 ? 0 : 6;
  const prevEndMonth = isS2 ? 5 : 11;

  return {
    startDate: formatDate(new Date(prevYear, prevStartMonth, 1)),
    endDate: formatDate(new Date(prevYear, prevEndMonth + 1, 0)),
  };
}

/**
 * Format date to YYYY-MM-DD string
 */
function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Parse an availability cutoff string (YYYY-MM-DD or mm/dd/yyyy) to a Date.
 * Returns undefined if the string is falsy or unparseable.
 */
function parseCutoff(availabilityCutoff?: string | null): Date | undefined {
  if (!availabilityCutoff) return undefined;
  // ISO date-only strings (YYYY-MM-DD) are parsed as UTC by default, which shifts the
  // date by one day in negative-offset timezones. Force local time by appending a time part.
  const str = /^\d{4}-\d{2}-\d{2}$/.test(availabilityCutoff)
    ? availabilityCutoff + "T00:00:00"
    : availabilityCutoff;
  const d = new Date(str);
  return isNaN(d.getTime()) ? undefined : d;
}

/**
 * Get start/end dates for a given time period, resolved against the dataset's
 * availability cutoff when provided. Falls back to current calendar date.
 */
export function getTimePeriodDates(
  timePeriod: string,
  availabilityCutoff?: string | null,
): TimePeriodDates | null {
  const cutoff = parseCutoff(availabilityCutoff) ?? new Date();
  switch (timePeriod) {
    case "latest_month":
    case "current_month":
      return getLatestMonthDates(cutoff);
    case "previous_month":
    case "last_month":
      return getPreviousMonthDates(cutoff);
    case "latest_quarter":
    case "current_quarter":
      return getLatestQuarterDates(cutoff);
    case "previous_quarter":
    case "last_quarter":
      return getPreviousQuarterDates(cutoff);
    case "latest_semester":
    case "current_semester":
      return getLatestSemesterDates(cutoff);
    case "previous_semester":
    case "last_semester":
      return getPreviousSemesterDates(cutoff);
    default:
      return null;
  }
}

/**
 * The window boundaries are:
 *   windowStart          — viewer's start date (or dataset's earliest data date, whichever is later)
 *   windowEnd            — viewer's end date already clamped to the dataset availability cutoff
 *   datasetEarliestDate  — (optional) the dataset's actual earliest data date (YYYY-MM-DD)
 *
 * A period is "fully contained" in the window when its start date falls on or after
 * windowStart. String comparison is safe here because dates are YYYY-MM-DD.
 *
 * Data-collection-start exception: if the dataset's earliest data date falls within the
 * resolved period (i.e. data collection began mid-period), that period is treated as
 * complete even though its calendar start precedes windowStart.
 *
 * Returns null when:
 *  - the period type is unrecognised (not a named quarter/semester)
 *  - no complete period exists before windowEnd
 *  - the resolved period starts before windowStart AND the exception does not apply
 */
export function resolveTimePeriodInWindow(
  timePeriod: string,
  windowStart: string,
  windowEnd: string,
  datasetEarliestDate?: string,
): TimePeriodDates | null {
  const cutoffDate = parseCutoff(windowEnd);
  if (!cutoffDate) return null;

  let resolved: TimePeriodDates | null = null;

  switch (timePeriod) {
    case "latest_month":
    case "current_month":
      resolved = getLatestMonthDates(cutoffDate);
      break;
    case "previous_month":
    case "last_month":
      resolved = getPreviousMonthDates(cutoffDate);
      break;
    case "latest_quarter":
    case "current_quarter":
      resolved = getLatestQuarterDates(cutoffDate);
      break;
    case "previous_quarter":
    case "last_quarter":
      resolved = getPreviousQuarterDates(cutoffDate);
      break;
    case "latest_semester":
    case "current_semester":
      resolved = getLatestSemesterDates(cutoffDate);
      break;
    case "previous_semester":
    case "last_semester":
      resolved = getPreviousSemesterDates(cutoffDate);
      break;
    default:
      return null;
  }

  if (!resolved) return null;

  // Reject if the period's start falls before the window start, unless the
  // data-collection-start exception applies: the dataset's earliest data date
  // falls within this period, meaning data collection began mid-period.
  if (resolved.startDate < windowStart) {
    const exceptionApplies =
      datasetEarliestDate &&
      datasetEarliestDate >= resolved.startDate &&
      datasetEarliestDate <= resolved.endDate;
    if (!exceptionApplies) return null;
  }

  return resolved;
}

type TimeUnit = "week" | "month" | "quarter" | "semester";

/**
 * Returns the start of the first complete period that begins on or after `dateStr`.
 * ISO weeks start on Monday; months on the 1st; quarters on Jan/Apr/Jul/Oct 1;
 * semesters on Jan/Jul 1.
 */
function snapToNextPeriodStart(dateStr: string, timeUnit: TimeUnit): string {
  const d = new Date(dateStr + "T00:00:00");
  switch (timeUnit) {
    case "week": {
      // JS getDay(): 0=Sun,1=Mon,...,6=Sat. Next Monday offset: (8 - day) % 7
      const offset = (8 - d.getDay()) % 7;
      const next = new Date(d);
      next.setDate(d.getDate() + offset);
      return formatDate(next);
    }
    case "month": {
      if (d.getDate() === 1) return dateStr;
      return formatDate(new Date(d.getFullYear(), d.getMonth() + 1, 1));
    }
    case "quarter": {
      const qStartMonth = Math.floor(d.getMonth() / 3) * 3;
      if (d.getDate() === 1 && d.getMonth() === qStartMonth) return dateStr;
      const nextQIdx = Math.floor(d.getMonth() / 3) + 1;
      const nextYear = d.getFullYear() + Math.floor(nextQIdx / 4);
      return formatDate(new Date(nextYear, (nextQIdx % 4) * 3, 1));
    }
    case "semester": {
      const sStartMonth = d.getMonth() < 6 ? 0 : 6;
      if (d.getDate() === 1 && d.getMonth() === sStartMonth) return dateStr;
      const nextSStartMonth = sStartMonth === 0 ? 6 : 0;
      const nextYear = sStartMonth === 6 ? d.getFullYear() + 1 : d.getFullYear();
      return formatDate(new Date(nextYear, nextSStartMonth, 1));
    }
  }
}

/**
 * Returns the end of the last complete period that ends on or before `dateStr`.
 * ISO weeks end on Sunday; months on the last calendar day; quarters on Mar/Jun/Sep/Dec
 * last day; semesters on Jun 30 or Dec 31.
 */
function snapToPreviousPeriodEnd(dateStr: string, timeUnit: TimeUnit): string {
  const d = new Date(dateStr + "T00:00:00");
  switch (timeUnit) {
    case "week": {
      // Sunday is getDay()===0. Subtract getDay() days to reach current/previous Sunday.
      const prev = new Date(d);
      prev.setDate(d.getDate() - d.getDay());
      return formatDate(prev);
    }
    case "month": {
      const lastDayOfMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      if (d.getDate() === lastDayOfMonth.getDate()) return dateStr;
      return formatDate(new Date(d.getFullYear(), d.getMonth(), 0));
    }
    case "quarter": {
      const qEndMonth = (Math.floor(d.getMonth() / 3) + 1) * 3 - 1;
      const qEndDay = new Date(d.getFullYear(), qEndMonth + 1, 0);
      if (d.getDate() === qEndDay.getDate() && d.getMonth() === qEndMonth)
        return dateStr;
      const prevQEndMonth = qEndMonth - 3;
      if (prevQEndMonth < 0)
        return formatDate(new Date(d.getFullYear() - 1, 12, 0));
      return formatDate(new Date(d.getFullYear(), prevQEndMonth + 1, 0));
    }
    case "semester": {
      const sEndMonth = d.getMonth() < 6 ? 5 : 11;
      const sEndDay = new Date(d.getFullYear(), sEndMonth + 1, 0);
      if (d.getDate() === sEndDay.getDate() && d.getMonth() === sEndMonth)
        return dateStr;
      if (sEndMonth === 5)
        return formatDate(new Date(d.getFullYear() - 1, 12, 0));
      return formatDate(new Date(d.getFullYear(), 6, 0));
    }
  }
}

/**
 * Trims a date range so it covers only complete periods of the given time unit.
 * Returns null when no complete period fits within the range (caller should treat
 * this as "no data" and skip the API request or render an empty state).
 */
export function trimDateRangeToCompletePeriods(
  startDate: string,
  endDate: string,
  timeUnit: TimeUnit,
): TimePeriodDates | null {
  const trimmedStart = snapToNextPeriodStart(startDate, timeUnit);
  const trimmedEnd = snapToPreviousPeriodEnd(endDate, timeUnit);
  if (trimmedStart > trimmedEnd) return null;
  return { startDate: trimmedStart, endDate: trimmedEnd };
}

/**
 * Resolve dates for a timePeriodSelection object against the dataset's availability cutoff.
 * Returns the selection unchanged for "all_time" and "custom_period".
 */
export function resolveTimePeriodDates<
  T extends {
    timePeriod: string;
    startDate: string | null;
    endDate: string | null;
  },
>(selection: T, availabilityCutoff?: string | null): T {
  if (!selection?.timePeriod) return selection;
  const resolved = getTimePeriodDates(selection.timePeriod, availabilityCutoff);
  if (!resolved) return selection;
  return {
    ...selection,
    startDate: resolved.startDate,
    endDate: resolved.endDate,
  };
}

/**
 * Map a named point-in-time period to the guardrail unit it belongs to.
 * Returns null for "all_time", "custom_period", or any unrecognized value.
 */
export function getPointInTimeUnitForPeriod(
  timePeriod: string,
): "month" | "quarter" | "semester" | null {
  switch (timePeriod) {
    case "latest_month":
    case "previous_month":
      return "month";
    case "latest_quarter":
    case "previous_quarter":
      return "quarter";
    case "latest_semester":
    case "previous_semester":
      return "semester";
    default:
      return null;
  }
}
